import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  input,
} from '@angular/core';
import { TwPulseDotComponent } from '@twist/ui-primitives';

/** Rendering treatment for {@link TwPageHeaderSubtitleComponent}. */
export type TwPageHeaderSubtitleVariant = 'dot' | 'chip' | 'labeled';

/** A single meta entry rendered in the subtitle strip. */
export interface TwPageHeaderMetaItem {
  /** The value text (the prominent part of the entry). */
  value: string;
  /** Optional dim leading label (e.g. `Location`). */
  label?: string;
  /** When `true`, renders an animated pulse dot before the value (gateway heartbeat). */
  pulse?: boolean;
}

/**
 * `tw-page-header-subtitle` — meta strip for {@link
 * import('./tw-page-header.component').TwPageHeaderComponent}.
 *
 * Two authoring modes:
 *
 * 1. **Declarative `[meta]` input** (default `dot` variant) — a sep-dot
 *    inline run, e.g. `📍 Brussels · Active since Mar 2024 · 22 devices ·
 *    ● Gateway online`. `chip` and `labeled` variants cover richer cases.
 * 2. **Projection** — author the row directly with literal
 *    `<span class="sep-dot"></span>` separators (the v1 contract; keeps the
 *    existing analytics-style markup working).
 *
 * Styling lives in the parent `tw-page-header.component.scss` via the
 * `.tw-page-header-subtitle*` BEM classes.
 */
@Component({
  selector: 'tw-page-header-subtitle',
  standalone: true,
  imports: [TwPulseDotComponent],
  templateUrl: './tw-page-header-subtitle.component.html',
  host: {
    '[class]': 'classes()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class TwPageHeaderSubtitleComponent {
  /** Declarative meta entries. When non-empty, rendered instead of projection. */
  readonly meta = input<TwPageHeaderMetaItem[] | null>(null);

  /** Treatment for the declarative `[meta]` run. */
  readonly variant = input<TwPageHeaderSubtitleVariant>('dot');

  protected readonly hasMeta = computed(() => {
    const m = this.meta();
    return !!m && m.length > 0;
  });

  protected readonly classes = computed(() =>
    ['tw-page-header-subtitle', `tw-page-header-subtitle--${this.variant()}`].join(' '),
  );
}
